import {
    MATCH_TYPE_EXACT_IGNORE_CASE,
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { isArray, isEmpty, isString } from "lodash-es";

import {
    RiskMapFilters,
    RiskMapColumn,
    BinaryAssessmentStatus,
    RiskMapStaticColumn,
    PerMetadataColumnFilter,
    MetadataColumnFilterValue,
    NodeType,
    Level,
    BinaryNodeScope,
    CellCoord,
    ControlId,
    ControlProgramId,
} from "api/generated/model";
import { IdAndName, OrFilter } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import {
    FilterMetadataColumnExtractorId,
    MetadataColumnExtractorId,
    MetadataColumnWithId,
} from "org/risks/content/types";
import {
    metadataExtractorIdRegex,
    METADATA_COLUMN_EXTRACTOR_ID_PREFIX,
} from "org/risks/content/utils";
import { FieldType, OptionValue, RISK_EXTRACTOR_KEYS } from "org/risks/types";
import { Guid } from "utils/guid";

import {
    createDecodeMatchRuleFn,
    createEncodeMatchRuleFn,
    decodeOrFilter,
    decodeOrFilterBase,
    encodeOrFilter,
    encodeOrFilterBase,
} from "../common";

type EntityId = number;
type RiskNodeId = Guid;
type MetadataColumnId = Guid;
type MetadataOptionValueId = Guid;

// Keep in sync with definition in impero_shareable_links/src/api/page_state/risk_maps.rs
const RISK_MAPS_STATIC_COLUMNS: RiskMapStaticColumn[] = [
    RISK_EXTRACTOR_KEYS.PATH,
    RISK_EXTRACTOR_KEYS.NAME,
    RISK_EXTRACTOR_KEYS.DESCRIPTION,
    RISK_EXTRACTOR_KEYS.ENTITIES,
    RISK_EXTRACTOR_KEYS.CONTROLS,
    RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
];

function isStaticColumn(arg: string): arg is RiskMapStaticColumn {
    return !!RISK_MAPS_STATIC_COLUMNS.find((column) => arg === column);
}

export function encodeRiskMapColumn(column: string): RiskMapColumn {
    if (isStaticColumn(column)) {
        return { type: "Static", value: column };
    } else {
        return { type: "Dynamic", value: column };
    }
}

function parseLevel(level: string): Level {
    function isLevel(level: string): level is Level {
        const allVariants: Array<Level> = ["riskDirectoryLevel", "entityLevel"];
        const allStr: Array<string> = allVariants;
        return allStr.includes(level);
    }

    if (isLevel(level)) {
        return level;
    } else {
        throw new Error(`${level} is not a valid Level value`);
    }
}

export function decodeRiskMapColumn(
    mapIdToName: Record<MetadataColumnId, string>,
    column: RiskMapColumn
): string {
    /* Example inputs:
    - metadata_entityLevel__d4235539-4131-4805-b46d-dea9613a0917_inherent%20impact
    - metadata_entityLevel__3430ce87-bb4a-4ce8-bfe3-84cfc5c85eed_inherent%20likelihood
    - metadata_riskDirectoryLevel__7870d55c-32a5-490f-b738-8a9a0fdbb3fc_financial%20statement%20assertions
    */
    function decodeColumn(
        id: string /* MetadataColumnExtractorId */
    ): IdAndName<MetadataColumnId, string> & {
        level: Level;
        isProcess: boolean;
    } {
        const match = metadataExtractorIdRegex.exec(id);
        if (match !== null) {
            const level = parseLevel(match[1]);
            const id = match[2] || match[3];
            const isProcess = match[2] !== "";
            const name = decodeURIComponent(match[4]);
            return { id, name, level, isProcess };
        } else {
            throw new Error(
                `ExtractorId "${id}" does not start with "${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}"`
            );
        }
    }

    switch (column.type) {
        case "Static":
            return column.value;
        case "Dynamic":
            const col = decodeColumn(column.value);
            const name = mapIdToName[col.id] || col.name;
            const [processId, riskId] = col.isProcess
                ? [col.id, ""]
                : ["", col.id];
            return `${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}${
                col.level
            }_${processId}_${riskId}_${encodeURIComponent(name)}`;
        default:
            return assertNever(column);
    }
}

// Filters

type EntityExtractorId =
    | typeof RISK_EXTRACTOR_KEYS.EXPANDED_RISK_NODE_ENTITY
    | typeof RISK_EXTRACTOR_KEYS.ENTITIES;
type RiskScreen = "RiskDirectory" | "RiskMap";

function encodeRiskMapFilters(
    screen: RiskScreen,
    filter: SimpleAndFilter | null
): RiskMapFilters {
    let filters: Array<SimpleOrFilter> = [];
    if (filter) {
        filters = filter.filters;
    }
    return {
        risk: encodeOrFilter<IdAndName<RiskNodeId, string>>(
            RISK_EXTRACTOR_KEYS.RISK,
            filters
        ),
        process: encodeOrFilter<IdAndName<RiskNodeId, string>>(
            RISK_EXTRACTOR_KEYS.PROCESSES,
            filters
        ),
        entity: encodeOrFilter<IdAndName<EntityId, string>>(
            screenToEntityExtractorId(screen),
            filters
        ),
        control: encodeOrFilter<IdAndName<ControlId, string>>(
            RISK_EXTRACTOR_KEYS.CONTROLS,
            filters
        ),
        controlProgram: encodeOrFilter<IdAndName<ControlProgramId, string>>(
            RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS,
            filters
        ),
        assessmentStatus: encodeOrFilter<BinaryAssessmentStatus>(
            RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
            filters
        ),
        scope: encodeOrFilter<BinaryNodeScope>(
            RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
            filters
        ),
        metadataColumns: encodeMetadataColumnFilters(filters),
    };
}

function isMetadataColumnExtractorId(extractorId: string): boolean {
    return (
        extractorId.startsWith(
            `process_${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}`
        ) ||
        extractorId.startsWith(`risk_${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}`)
    );
}

function encodeMetadataColumnFilters(
    filters: Array<SimpleOrFilter>
): Record<MetadataColumnId, PerMetadataColumnFilter> | undefined {
    const encodeFilter = createEncodeMatchRuleFn<
        IdAndName<MetadataOptionValueId, string> | string,
        MetadataColumnFilterValue
    >(MATCH_TYPE_EXACT_IGNORE_CASE, (value) => {
        if (isString(value)) {
            return { type: "Value", value };
        } else if (isArray(value)) {
            console.error("value = ", value);
            throw new Error("Can't encode arrays, an expander is missing");
        } else {
            return { type: "Id", value };
        }
    });

    const output: Record<MetadataColumnId, PerMetadataColumnFilter> = {};
    const metadataColumnFilters = filters.filter((f) =>
        isMetadataColumnExtractorId(f.extractorId)
    );
    for (const orFilter of metadataColumnFilters) {
        const {
            id: columnId,
            name,
            nodeType,
            level,
        } = extractorIdToColumnIdAndName(
            parseFilterMetadataColumnExtractorId(orFilter.extractorId)
        );
        const filter = encodeOrFilterBase(
            encodeFilter,
            orFilter.extractorId,
            filters
        );
        const encodedFilter =
            filter !== undefined
                ? { filter, name, nodeType, level }
                : undefined;
        if (encodedFilter) {
            output[columnId] = encodedFilter;
        }
    }
    return isEmpty(output) ? undefined : output;
}

function parseFilterMetadataColumnExtractorId(
    s: string
): FilterMetadataColumnExtractorId {
    function isFilterMetadataColumnExtractorId(
        s: string
    ): s is FilterMetadataColumnExtractorId {
        return filterMetadataExtractorIdRegex.test(s);
    }

    if (isFilterMetadataColumnExtractorId(s)) {
        return s;
    } else {
        throw new Error(
            `${s} is not a valid FilterMetadataColumnExtractorId value`
        );
    }
}

const filterMetadataExtractorIdRegex =
    /^(risk|process)_metadata_(riskDirectoryLevel|entityLevel)_([\w-]+)_(.+)$/;

function extractorIdToColumnIdAndName(
    extractorId: FilterMetadataColumnExtractorId
): { id: MetadataColumnId; name: string; nodeType: NodeType; level: Level } {
    function parseNodeType(s: string): NodeType {
        function isNodeType(s: string): s is NodeType {
            const allVariants: Array<NodeType> = ["risk", "process"];
            const allStr: Array<string> = allVariants;
            return allStr.includes(s);
        }

        if (isNodeType(s)) {
            return s;
        } else {
            throw new Error(`${s} is not a valid NodeType value`);
        }
    }

    const match = filterMetadataExtractorIdRegex.exec(extractorId);
    if (match !== null) {
        const nodeType = parseNodeType(match[1]);
        const level = parseLevel(match[2]);
        const id = match[3];
        const name = decodeURIComponent(match[4]);
        return { id, name, nodeType, level };
    } else {
        throw new Error(
            `ExtractorId "${extractorId}" does not start with "(risk|process)_${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}"`
        );
    }
}

function columnIdAndNameToExtractorId(
    idAndName: IdAndName<MetadataColumnId, string>,
    nodeType: NodeType,
    level: Level
): FilterMetadataColumnExtractorId {
    const { id, name } = idAndName;
    return `${nodeType}_${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}${level}_${id}_${encodeURIComponent(
        name
    )}`;
}

function decodeMetadataColumnFilters(
    mapIdToName: Record<MetadataColumnId, string>,
    mapOptionIdToName: Record<MetadataOptionValueId, string>,
    metadataColumnFiltersPerCategoryId: Record<
        MetadataColumnId,
        PerMetadataColumnFilter
    >
): Array<SimpleOrFilter> {
    const decodeFilter = createDecodeMatchRuleFn<
        IdAndName<MetadataOptionValueId, string> | string,
        MetadataColumnFilterValue
    >(MATCH_TYPE_EXACT_IGNORE_CASE, (value) => {
        if (value.type === "Id") {
            const { id, name } = value.value;
            return { id, name: mapOptionIdToName[id] || name };
        } else {
            return value.value;
        }
    });

    const output: Array<SimpleOrFilter> = [];
    for (const [columnId, { name, filter, nodeType, level }] of Object.entries(
        metadataColumnFiltersPerCategoryId
    )) {
        const idAndName = { id: columnId, name: mapIdToName[columnId] || name };
        const extractorId = columnIdAndNameToExtractorId(
            idAndName,
            nodeType,
            level
        );
        output.push(decodeOrFilterBase(decodeFilter, extractorId, filter));
    }
    return output;
}

// Also works for single-entity views on these screens
function screenToEntityExtractorId(screen: RiskScreen): string {
    switch (screen) {
        case "RiskDirectory":
            return RISK_EXTRACTOR_KEYS.ENTITIES;
        case "RiskMap":
            return RISK_EXTRACTOR_KEYS.EXPANDED_RISK_NODE_ENTITY;
        default:
            assertNever(screen);
    }
}

function decodeRiskMapPageFilters(
    mapIdToName: Record<MetadataColumnId, string>,
    mapOptionIdToName: Record<MetadataOptionValueId, string>,
    filters: RiskMapFilters,
    screen: RiskScreen
): SimpleAndFilter {
    const output: Array<SimpleOrFilter> = [];

    const decode = <T>(
        extractorId: string,
        filter: OrFilter<T> | null | undefined
    ) => {
        if (filter) {
            output.push(decodeOrFilter(extractorId, filter));
        }
    };

    const entityExtractorId = screenToEntityExtractorId(screen);

    decode<IdAndName<RiskNodeId, string>>(
        RISK_EXTRACTOR_KEYS.RISK,
        filters.risk
    );
    decode<IdAndName<RiskNodeId, string>>(
        RISK_EXTRACTOR_KEYS.PROCESSES,
        filters.process
    );
    decode<IdAndName<EntityId, string>>(entityExtractorId, filters.entity);
    decode<IdAndName<ControlId, string>>(
        RISK_EXTRACTOR_KEYS.CONTROLS,
        filters.control
    );
    decode<IdAndName<ControlProgramId, string>>(
        RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS,
        filters.controlProgram
    );
    decode<BinaryAssessmentStatus>(
        RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
        filters.assessmentStatus
    );
    decode<BinaryNodeScope>(RISK_EXTRACTOR_KEYS.FILTER_SCOPE, filters.scope);

    if (filters.metadataColumns) {
        output.push(
            ...decodeMetadataColumnFilters(
                mapIdToName,
                mapOptionIdToName,
                filters.metadataColumns
            )
        );
    }

    return { filters: output };
}

// Page state

export function computeIdToNameMaps(
    customColumns: MetadataColumnWithId<MetadataColumnExtractorId>[]
): [Record<MetadataColumnId, string>, Record<MetadataOptionValueId, string>] {
    const mapIdToName: Record<MetadataColumnId, string> = {};
    const mapOptionIdToName: Record<MetadataOptionValueId, string> = {};

    function fieldTypeOptions(fieldType: FieldType): OptionValue[] {
        switch (fieldType.type) {
            case "Dropdown":
            case "RadioButtonGroup":
            case "CheckboxGroup":
                return fieldType.options;
            case "TextArea":
            case "NumericInput":
            case "TextInput":
            case "Heading":
            case "AttachedFiles":
                return [];
            default:
                return assertNever(fieldType);
        }
    }

    customColumns.forEach((col) => {
        for (const field of Object.values(col.fields)) {
            mapIdToName[field.id] = field.name.trim().toLocaleLowerCase(); // Match normalizeColumn

            const fieldType = field.fieldType;
            fieldTypeOptions(fieldType).forEach((option) => {
                mapOptionIdToName[option.id] = option.value;
            });
        }
    });

    return [mapIdToName, mapOptionIdToName];
}

interface RiskMapPageStateRaw extends PageFilterAndColumns {
    hideHeatmap: boolean;
    showResidualHeatmap: boolean;
    showTableView: boolean;
    focusFilterCoords: CellCoord[];
}

export {
    encodeRiskMapFilters,
    decodeRiskMapPageFilters,
    RISK_MAPS_STATIC_COLUMNS,
};
export type {
    EntityExtractorId,
    EntityId,
    MetadataColumnId,
    MetadataOptionValueId,
    RiskMapPageStateRaw,
    RiskNodeId,
};
